import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  ReportLevel,
  ReportResponse,
  ReportStatus,
  ReportStatusResponse,
  ReportVersionSummary,
} from '@/types/report';
import { toError } from '@/utils/errors';
import { usePoll } from './usePoll';

const DEFAULT_POLL_INTERVAL = 5000; // 5 seconds
const MAX_POLL_DURATION = 600000; // 10 minutes

/**
 * Statuses where the report is renderable right now.
 *
 * `in_qa` belongs here: the content has landed and is only waiting on the
 * admin approve action, so a reviewer must be able to read it. `triage` /
 * `pending_activation` render their own cards (identification picker,
 * activation prompt) rather than content. `submitted` is the coarse
 * client spelling of `triage`.
 */
const RENDERABLE_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>([
  'completed',
  'in_qa',
  'triage',
  'submitted',
  'pending_activation',
]);

/**
 * Statuses where work is in flight — keep polling until it settles.
 * `in_progress` is the coarse client stage covering all of them. Every
 * member is machine-paced, which is what makes the ten-minute poll cap a
 * fair "something is stuck" signal.
 */
const IN_FLIGHT_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>([
  'queued',
  'processing',
  'processing_feedback',
  'in_progress',
]);

/**
 * Statuses parked on a person: nothing to render yet and nothing worth
 * polling for on a human timescale. The viewer holds its waiting state
 * (a plain loader) and the report page's alerts and aside tabs carry the
 * action; a submit remounts the viewer, which re-fetches and resumes
 * polling on the next in-flight stage.
 *
 * `awaiting_profile_review` belongs here, not in flight: the reviewer is
 * told to work on this very page, often for longer than the poll cap,
 * and a lapsed cap would replace the gate with a timeout error and a
 * Sentry event for a report that is healthy.
 */
const PARKED_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>([
  'awaiting_fulfilment',
  'awaiting_file_selection',
  'awaiting_profile_review',
  'awaiting_match_review',
]);

export type ReportFetchState = 'idle' | 'fetching' | 'polling' | 'success' | 'error' | 'not_found';

interface UseReportOptions {
  /** Report level filter */
  level?: ReportLevel;
  /** Polling interval in milliseconds @default 5000 */
  pollInterval?: number;
  /** Callback when report completes successfully */
  onSuccess?: (report: ReportResponse) => void;
  /** Callback when report fails */
  onError?: (error: Error) => void;
  /** Whether to automatically fetch on mount @default true */
  autoFetch?: boolean;
}

interface UseReportReturn {
  /** Current fetch/poll state */
  state: ReportFetchState;
  /** The report data (when completed) */
  report: ReportResponse | null;
  /** Report content shortcut */
  content: string | null;
  /** Current status from API */
  status: ReportStatus | null;
  /** Error message */
  error: string | null;
  /** Whether a fetch/poll is in progress */
  isLoading: boolean;
  /** Whether currently polling */
  isPolling: boolean;
  /** Report ID (if available) */
  reportId: string | null;
  /** Run ID for feedback panel */
  runId: string | null;
  /** Manually fetch the report by subject ID (latest) */
  fetchReport: (subjectId: string) => Promise<void>;
  /** Manually fetch a specific report by report ID */
  fetchReportById: (reportId: string) => Promise<void>;
  /** Cancel polling */
  cancel: () => void;
  /** Fetch all versions */
  fetchVersions: () => Promise<ReportVersionSummary[]>;
}

/** One status tick, resolved to what the page must do about it. Two-hop:
 * a renderable status fetches the full report inside the same tick. */
type StatusTick =
  | { kind: 'in_flight'; status: ReportStatus }
  | { kind: 'parked'; status: ReportStatus }
  | { kind: 'renderable'; status: ReportStatus; report: ReportResponse }
  | { kind: 'failed'; status: ReportStatus; error: string }
  | { kind: 'not_found' };

/** Report read endpoints are team-scoped server-side; a 404 means "not
 * yours / no longer accessible", not a failure. */
function isNotFound(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err.message.includes('404') || err.message.includes('Report not found'))
  );
}

/**
 * Unified hook for fetching reports (screening or escalated).
 *
 * Handles the full report lifecycle:
 * - Fetches the latest report via GET /reports/subjects/{id}/latest
 * - If queued/processing: polls GET /reports/{id}/status every 5s
 * - If completed: returns content, stops polling
 * - If failed: returns error, stops polling
 *
 * @example
 * ```tsx
 * const { state, content, error, runId } = useReport(subjectId, { level: 'screening' });
 * ```
 */
export function useReport(subjectId: string, options: UseReportOptions = {}): UseReportReturn {
  const {
    level,
    pollInterval = DEFAULT_POLL_INTERVAL,
    onSuccess,
    onError,
    autoFetch = true,
  } = options;

  const [state, setState] = useState<ReportFetchState>('idle');
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [status, setStatus] = useState<ReportStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  /** The report whose status is being polled; null = not polling. */
  const [pollTarget, setPollTarget] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  // Callback refs for stable references
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  const fail = useCallback((message: string) => {
    setError(message);
    setState('error');
    setPollTarget(null);
    const err = new Error(message);
    onErrorRef.current?.(err);
    Sentry.captureException(err);
  }, []);

  usePoll<StatusTick>({
    enabled: pollTarget !== null,
    interval: pollInterval,
    maxDurationMs: MAX_POLL_DURATION,
    refetchDeps: [pollTarget],
    queryFn: async ({ signal }) => {
      if (!pollTarget) {
        throw new Error('no report to poll');
      }
      try {
        const statusResponse: ReportStatusResponse = await vnextApiClient.getReportStatus(
          pollTarget,
          { signal }
        );
        // Non-admins never see the fine-grained working statuses (or
        // 'failed'; masked rows poll forever by design, the analyst is
        // flagged internally) — the coarse 'in_progress' covers them.
        if (IN_FLIGHT_STATUSES.has(statusResponse.status)) {
          return { kind: 'in_flight', status: statusResponse.status };
        }
        if (PARKED_STATUSES.has(statusResponse.status)) {
          // Reached mid-poll (processing → a human gate while someone was
          // already watching): stop polling and hold, never time out.
          return { kind: 'parked', status: statusResponse.status };
        }
        if (RENDERABLE_STATUSES.has(statusResponse.status)) {
          // These states are terminal for polling — the page branches on
          // report.status to render the identification picker (triage) or
          // the activation card (pending_activation).
          const fullReport = await vnextApiClient.getReport(pollTarget, { signal });
          return { kind: 'renderable', status: statusResponse.status, report: fullReport };
        }
        if (statusResponse.status === 'failed') {
          return {
            kind: 'failed',
            status: statusResponse.status,
            error: statusResponse.error || 'Report processing failed',
          };
        }
        return { kind: 'in_flight', status: statusResponse.status }; // unknown: keep polling
      } catch (err) {
        if (isNotFound(err)) {
          return { kind: 'not_found' };
        }
        throw err;
      }
    },
    until: (tick) => tick.kind !== 'in_flight',
    onData: (tick) => {
      switch (tick.kind) {
        case 'in_flight':
          setStatus(tick.status);
          break;
        case 'parked':
          setStatus(tick.status);
          setState('fetching');
          setPollTarget(null);
          break;
        case 'renderable':
          setStatus(tick.status);
          setReport(tick.report);
          setState('success');
          setPollTarget(null);
          onSuccessRef.current?.(tick.report);
          break;
        case 'failed':
          setStatus(tick.status);
          fail(tick.error);
          break;
        case 'not_found':
          setState('not_found');
          setPollTarget(null);
          break;
      }
    },
    // A failed tick is not a failed report: the poll backs off and tries
    // again (bounded by the ten-minute cap).
    onError: (err) => Sentry.captureException(toError(err)),
    onStop: (reason) => {
      if (reason === 'maxDuration') {
        fail('Report fetch operation timed out after 10 minutes');
      }
    },
  });

  const cancel = useCallback(() => {
    setPollTarget(null);
    setState('idle');
    setStatus(null);
  }, []);

  /** Route a freshly fetched report to render / poll / fail. */
  const settle = useCallback((response: ReportResponse) => {
    setReportId(response.id);
    setStatus(response.status);

    if (RENDERABLE_STATUSES.has(response.status)) {
      setReport(response);
      setState('success');
      onSuccessRef.current?.(response);
    } else if (IN_FLIGHT_STATUSES.has(response.status)) {
      setState('polling');
      setPollTarget(response.id);
    } else if (response.status === 'failed') {
      const errorMsg = response.error || 'Report processing failed';
      setError(errorMsg);
      setState('error');
      onErrorRef.current?.(new Error(errorMsg));
    } else if (PARKED_STATUSES.has(response.status)) {
      // Waiting on a person: hold the waiting state without a poll (and
      // so without the poll cap).
      setState('fetching');
    } else {
      // Unknown stage: hold rather than poll. Explicit so a status can
      // never again fall through every branch unnoticed — that is what
      // left `in_qa` reports spinning forever.
      setState('fetching');
    }
  }, []);

  const failFetch = useCallback((err: unknown, fallback: string) => {
    if (isNotFound(err)) {
      setState('not_found');
      return;
    }
    const e = toError(err, fallback);
    setError(e.message);
    setState('error');
    onErrorRef.current?.(e);
    Sentry.captureException(err);
  }, []);

  // Main fetch function
  const fetchReport = useCallback(
    async (fetchSubjectId: string) => {
      try {
        // Reset state
        setState('fetching');
        setStatus(null);
        setReport(null);
        setError(null);
        setReportId(null);
        setPollTarget(null);

        const response = await vnextApiClient.getLatestReport(fetchSubjectId, level);
        if (!isMountedRef.current) {
          return;
        }
        settle(response);
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }
        failFetch(err, 'Failed to fetch report');
      }
    },
    [level, settle, failFetch]
  );

  // Fetch a specific report by its ID
  const fetchReportById = useCallback(
    async (fetchId: string) => {
      try {
        setState('fetching');
        setStatus(null);
        setReport(null);
        setError(null);
        setReportId(fetchId);
        setPollTarget(null);

        const response = await vnextApiClient.getReport(fetchId);
        if (!isMountedRef.current) {
          return;
        }
        settle(response);
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }
        failFetch(err, 'Failed to fetch report');
      }
    },
    [settle, failFetch]
  );

  // Fetch versions
  const fetchVersions = useCallback(async (): Promise<ReportVersionSummary[]> => {
    if (!subjectId) {
      return [];
    }
    return vnextApiClient.getReportVersions(subjectId, level);
  }, [subjectId, level]);

  // Auto-fetch on mount / subjectId change
  const fetchedSubjectRef = useRef<string | null>(null);
  useEffect(() => {
    if (autoFetch && subjectId && fetchedSubjectRef.current !== subjectId) {
      fetchedSubjectRef.current = subjectId;
      fetchReport(subjectId);
    }
  }, [autoFetch, subjectId, fetchReport]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    state,
    report,
    content: report?.content ?? null,
    status,
    error,
    isLoading: state === 'fetching' || state === 'polling',
    isPolling: state === 'polling',
    reportId,
    runId: report?.run_id ?? null,
    fetchReport,
    fetchReportById,
    cancel,
    fetchVersions,
  };
}
